import { fetchAuthSession, getCurrentUser, signInWithRedirect } from 'aws-amplify/auth';
import { Cache } from 'aws-amplify/utils';
import LoadingPlaceholderSkeletonPage from 'components/Loading/LoadingPlaceholderPage/LoadingPlaceholderSkeletonPage';
import useAuth, { AuthContextTypes, AuthStateTypes } from 'hooks/useAuth.hook';
import useBroadcastChannel from 'hooks/useBroadcastChannel';
import { createContext, ReactNode, useEffect, useRef, useState } from 'react';
import config from 'react-global-configuration';
import { useSearchParams } from 'react-router-dom';
import { LocalAuth } from 'utils/_localAuth';
import { getIdToken, tokenHasExpired } from 'utils/utils';

export const AuthenticationContext = createContext({} as AuthContextTypes);
type AuthenticationProviderProps = { children: ReactNode };

const AuthenticationProvider = ({ children }: AuthenticationProviderProps) => {
  const [userAuthChecked, setUserAuthChecked] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const { authState, setAuthState, amplify } = useAuth();
  const { initialiseBroadcastMessageEventListener } = useBroadcastChannel();

  const [params] = useSearchParams();
  const isRedirectingRef = useRef(false);

  const value: any = { authState, setAuthState };

  function handleAuthError(e: any, initiateRedirect: boolean = true) {
    if (isRedirectingRef.current) return;

    setIsAuthenticating(false);
    setUserAuthChecked(false);

    if (initiateRedirect) {
      isRedirectingRef.current = true;
      signInWithRedirect();
    }
  }

  async function getToken() {
    if (params.get("code")) {
      LocalAuth.tokenRequest(params.get("code") || "").then((res) => {
        window.location.href = "/"
      })
    }

  }
  useEffect(() => {

    initialiseBroadcastMessageEventListener()

    if (authState.isMock) {
      Cache.setItem('authState', authState);
      setIsAuthenticating(false);
      setUserAuthChecked(true);
      return
    }

    const checkUser = async () => {
      setIsAuthenticating(true);

      if (config.get().SHOULD_USE_COGNITO) {
        try {
          const session = await fetchAuthSession();
          if (!session.tokens?.accessToken) {
            handleAuthError("No valid session found");
            return;
          }

          const user = await getCurrentUser();
          const newState = {
            ...user,
            isAuthenticated: true,
            jwtToken: session.tokens.accessToken?.toString() ?? ""
          };

          setAuthState(newState);
          Cache.setItem("authState", newState);

          // Important: Clean up the code parameter after a successful exchange
          if (params.get("code")) {
            window.location.href = "/"
          }

        } catch (error) {
          // If fetchAuthSession throws a hard error (e.g., network), redirect.
          handleAuthError(error);
          return;
        }
      }
      else {
        if (window.location.toString().indexOf('local-login') === -1) {
          amplify.currentAuthenticatedUser()
            .then((session: AuthStateTypes) => {
              const token = getIdToken(session);
              if (!token || tokenHasExpired(token)) {
                amplify.signInWithRedirect();
                return;
              }
              setAuthState(session);
              Cache.setItem('authState', session);
            })
            .catch(() => {
              amplify.signInWithRedirect();
            })
        } else {
          getToken();
          return;
        }
      }

      setIsAuthenticating(false);
      setUserAuthChecked(true);
    };

    checkUser()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  return userAuthChecked && !isAuthenticating ? (
    <AuthenticationContext.Provider value={value}>{children}</AuthenticationContext.Provider>
  ) : (
    <LoadingPlaceholderSkeletonPage />
  );
};

export default AuthenticationProvider;
